/**
 * Replaces computed properties in an object with their current values.
 *
 * This is necessary because the Redux DevTools extension does not support computed properties.
 * Depending on when the store is serialized, the computed properties may not have access to state.
 *
 * This function ensures the computed properties have access to the store's state before it assigns its value.
 *
 * @param key - The key of the property being stringified.
 * @param value - The value of the property being stringified.
 * @returns The replaced value if the property is a computed property, otherwise the original value.
 */
export const devToolComputedValueReplacer = (key: string, value: unknown) => {
  // getters are nested under the computed key within stores.
  // ensures that we target the computed key and that its value is an object
  if (key === 'computed' && typeof value === 'object' && value !== null) {
    const replacedValue: Record<string, unknown> = {};

    // eslint-disable-next-line guard-for-in
    for (const nestedKey in value) {
      const getterDescriptor = Object.getOwnPropertyDescriptor(
        value,
        nestedKey,
      );

      if (getterDescriptor && getterDescriptor.get) {
        try {
          replacedValue[nestedKey] = getterDescriptor.get.call(value);

          // eslint-disable-next-line no-empty
        } catch {}
      }
    }

    return replacedValue;
  }

  return value;
};
